import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { IPayrollState } from './payroll.state';
import {
  AllowanceTypeApi,
  ContributionSettingApi,
  DeductionTypeApi,
  PayrollPeriodApi,
  PayrollScheduleApi,
  TaxBracketApi,
} from '../../models/payroll/payroll-config.model';
import {
  EmployeeDtrStatusApi,
  PayrollResultApi,
  PayrollRunApi,
  PayslipApi,
} from '../../models/payroll/payroll-run.model';
import { EmployeeDeductionApi } from '../../models/payroll/employee-deduction.model';

function keepFinishedOverLateProcessing(current: number, incoming: number): boolean {
  const finished = current === 3 || current === 4;
  return finished && incoming === 2;
}

export const withPayrollStateMethods = signalStoreFeature(
  { state: type<IPayrollState>() },
  withMethods((store) => ({
    setSchedules: (schedules: PayrollScheduleApi[]) => patchState(store, { schedules, loading: false, error: null }),
    setPeriods: (periods: PayrollPeriodApi[]) => patchState(store, { periods, loading: false, error: null }),
    setRuns: (runs: PayrollRunApi[]) => patchState(store, { runs, loading: false, error: null }),
    setSelectedRun: (selectedRun: PayrollRunApi | null) => patchState(store, { selectedRun, loading: false, error: null }),
    setResults: (results: PayrollResultApi[]) => patchState(store, { results, loading: false, error: null }),
    setPayslips: (payslips: PayslipApi[]) => patchState(store, { payslips, loading: false, error: null }),
    setAllowanceTypes: (allowanceTypes: AllowanceTypeApi[]) => patchState(store, { allowanceTypes, loading: false, error: null }),
    setDeductionTypes: (deductionTypes: DeductionTypeApi[]) => patchState(store, { deductionTypes, loading: false, error: null }),
    setContributionSettings: (contributionSettings: ContributionSettingApi[]) =>
      patchState(store, { contributionSettings, loading: false, error: null }),
    setTaxBrackets: (taxBrackets: TaxBracketApi[]) => patchState(store, { taxBrackets, loading: false, error: null }),
    setEmployeeDeductions: (employeeDeductions: EmployeeDeductionApi[]) =>
      patchState(store, { employeeDeductions, loading: false, error: null }),
    setDtrStatuses: (dtrStatuses: EmployeeDtrStatusApi[]) => patchState(store, { dtrStatuses, loading: false, error: null }),
    addRun: (run: PayrollRunApi) => patchState(store, { runs: [run, ...store.runs()], loading: false, error: null }),
    updateRun: (run: PayrollRunApi) => {
      const current = store.runs().find((r) => r.payrollRunGuid === run.payrollRunGuid);
      const selected = store.selectedRun();
      const nextRun = current && keepFinishedOverLateProcessing(current.status, run.status) ? current : run;
      const nextSelected = selected?.payrollRunGuid === run.payrollRunGuid
        ? (keepFinishedOverLateProcessing(selected.status, run.status) ? selected : run)
        : selected;
      patchState(store, {
        runs: store.runs().map((r) => (r.payrollRunGuid === run.payrollRunGuid ? nextRun : r)),
        selectedRun: nextSelected,
        loading: false,
        error: null,
      });
    },
    addSchedule: (schedule: PayrollScheduleApi) =>
      patchState(store, { schedules: [schedule, ...store.schedules()], loading: false, error: null }),
    updateSchedule: (schedule: PayrollScheduleApi) =>
      patchState(store, {
        schedules: store.schedules().map((s) => (s.payrollScheduleGuid === schedule.payrollScheduleGuid ? schedule : s)),
        loading: false,
        error: null,
      }),
    addPeriod: (period: PayrollPeriodApi) =>
      patchState(store, { periods: [period, ...store.periods()], loading: false, error: null }),
    updatePeriod: (period: PayrollPeriodApi) =>
      patchState(store, {
        periods: store.periods().map((p) => (p.payrollPeriodGuid === period.payrollPeriodGuid ? period : p)),
        loading: false,
        error: null,
      }),
    addAllowanceType: (item: AllowanceTypeApi) =>
      patchState(store, { allowanceTypes: [item, ...store.allowanceTypes()], loading: false, error: null }),
    removeAllowanceType: (guid: string) =>
      patchState(store, {
        allowanceTypes: store.allowanceTypes().filter((a) => a.allowanceTypeGuid !== guid),
        loading: false,
        error: null,
      }),
    addDeductionType: (item: DeductionTypeApi) =>
      patchState(store, { deductionTypes: [item, ...store.deductionTypes()], loading: false, error: null }),
    removeDeductionType: (guid: string) =>
      patchState(store, {
        deductionTypes: store.deductionTypes().filter((d) => d.deductionTypeGuid !== guid),
        loading: false,
        error: null,
      }),
    updateContributionSetting: (setting: ContributionSettingApi) =>
      patchState(store, {
        contributionSettings: store.contributionSettings().map((s) =>
          s.contributionSettingGuid === setting.contributionSettingGuid ? setting : s
        ),
        loading: false,
        error: null,
      }),
    addEmployeeDeduction: (deduction: EmployeeDeductionApi) =>
      patchState(store, { employeeDeductions: [deduction, ...store.employeeDeductions()], loading: false, error: null }),
    updateEmployeeDeduction: (deduction: EmployeeDeductionApi) =>
      patchState(store, {
        employeeDeductions: store.employeeDeductions().map((d) =>
          d.employeeDeductionGuid === deduction.employeeDeductionGuid ? deduction : d
        ),
        loading: false,
        error: null,
      }),
    setError: (error: string) => patchState(store, { error, loading: false }),
  }))
);
